import { useState, useEffect, useRef, useCallback } from 'react'

import { Document } from 'react-pdf'
import { Page, pdfjs } from 'react-pdf/dist/esm/entry.webpack'
import styled from 'styled-components'

import { media } from '../../../styles/breakpoints'

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

interface ReportsDocumentProps {
  file?: string
}

const ReportDocumentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  flex: 1;
  min-height: 0;
  width: 100%;

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
    margin-bottom: 10px;
    flex-shrink: 0;
    font-size: 13px;
    color: #111827;

    .page-indicator {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 120px;
      padding: 8px 10px;
      border-radius: 6px;
      border: 1px solid #e5e7eb;
      background: #f9fafb;
    }

    button {
      border: 1px solid #d1d5db;
      border-radius: 6px;
      padding: 8px 10px;
      cursor: pointer;
      background: #ffffff;
      color: #111827;
      font-weight: 500;
    }

    button:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    @media ${media.belowTablet} {
      button {
        min-height: 44px;
        min-width: 44px;
        padding: 10px 14px;
      }

      .page-indicator {
        min-height: 44px;
        flex: 1;
        min-width: 0;
      }
    }
  }

  .document-container {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
    min-height: 0;
    flex: 1;
    overflow: auto;
    position: relative;
    cursor: grab;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #f3f4f6;
    padding: 12px;
    padding-bottom: 24px;
    box-sizing: border-box;
    touch-action: none;
  }

  .document-container:active {
    cursor: grabbing;
  }

  .document-content {
    transform-origin: top left;
    transition: transform 0.1s ease-out;
  }

  .loading-indicator {
    display: flex;
    justify-content: center;
    align-items: center;
    position: fixed;
    left: 50%;
    transform: translate(-50%, 200px);
    font-size: 18px;
    color: rgba(0, 0, 0, 0.7);
  }
`

export const ReportsDocument = ({ file }: ReportsDocumentProps) => {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [scale, setScale] = useState(1)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const [startPan, setStartPan] = useState({ x: 0, y: 0 })
  const [isDeepZoom, setIsDeepZoom] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const panPointerIdRef = useRef<number | null>(null)

  const deepZoomLevel = 2

  const onDocumentLoadSuccess = ({
    numPages: totalPages,
  }: {
    numPages: number
  }) => {
    setNumPages(totalPages)
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [file])

  const beginPan = useCallback(
    (clientX: number, clientY: number) => {
      setIsPanning(true)
      setStartPan({
        x: clientX - translate.x,
        y: clientY - translate.y,
      })
    },
    [translate.x, translate.y],
  )

  const movePan = useCallback(
    (clientX: number, clientY: number) => {
      if (!isPanning) return
      setTranslate({
        x: clientX - startPan.x,
        y: clientY - startPan.y,
      })
    },
    [isPanning, startPan.x, startPan.y],
  )

  const endPan = useCallback(() => {
    setIsPanning(false)
    panPointerIdRef.current = null
  }, [])

  const handleMouseDown = (event: React.MouseEvent) => {
    beginPan(event.clientX, event.clientY)
  }

  const handleMouseMove = (event: React.MouseEvent) => {
    movePan(event.clientX, event.clientY)
  }

  const handleTouchStart = (event: React.TouchEvent) => {
    if (event.touches.length !== 1) return
    const touch = event.touches[0]
    panPointerIdRef.current = touch.identifier
    beginPan(touch.clientX, touch.clientY)
  }

  const handleTouchMove = (event: React.TouchEvent) => {
    if (!isPanning || panPointerIdRef.current == null) return
    const touch = Array.from(event.touches).find(
      t => t.identifier === panPointerIdRef.current,
    )
    if (!touch) return
    event.preventDefault()
    movePan(touch.clientX, touch.clientY)
  }

  const toggleZoom = () => {
    if (isDeepZoom) {
      setScale(1)
      setIsDeepZoom(false)
    } else {
      setScale(deepZoomLevel)
      setIsDeepZoom(true)
    }
    setTranslate({ x: 0, y: 0 })
  }

  const resetZoom = () => {
    setScale(1)
    setTranslate({ x: 0, y: 0 })
    setIsDeepZoom(false)
  }

  return (
    <ReportDocumentWrapper>
      {file && numPages && (
        <div className="toolbar">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage <= 1}
          >
            Previous
          </button>
          <div className="page-indicator">
            Page {currentPage} of {numPages}
          </div>
          <button
            onClick={() =>
              setCurrentPage(prev => Math.min(prev + 1, numPages || 1))
            }
            disabled={currentPage >= numPages}
          >
            Next
          </button>
          <button onClick={toggleZoom}>
            {isDeepZoom ? 'Zoom Out' : 'Zoom In'}
          </button>
          <button onClick={resetZoom}>Reset</button>
        </div>
      )}
      <div
        className="document-container"
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={endPan}
        onMouseLeave={endPan}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={endPan}
        onTouchCancel={endPan}
      >
        <div
          className="document-content"
          style={{
            transform: `scale(${scale}) translate(${translate.x}px, ${translate.y}px)`,
          }}
        >
          <Document
            key={file}
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div className="loading-indicator">
                <div>Loading...</div>
              </div>
            }
            noData={
              <div className="loading-indicator">
                <div>Preparing...</div>
              </div>
            }
            options={{
              cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
              cMapPacked: true,
            }}
          >
            {file && <Page pageNumber={currentPage} />}
          </Document>
        </div>
      </div>
    </ReportDocumentWrapper>
  )
}
